import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FEEDBACK_WRITE_SCOPE,
  defaultIntegrationScopesForKind,
  integrationScopesForKind,
  type IntegrationScope,
  type IntegrationTokenCreatedDto,
  type IntegrationTokenDto,
} from '@aiai/shared';
import {
  createIntegrationToken,
  listIntegrationTokens,
  revokeIntegrationToken,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { IntegrationWebhookPanel } from './IntegrationWebhookPanel';
import { ConfirmDialog } from './ConfirmDialog';
import { CopyIntegrationPromptButton } from './CopyIntegrationPromptButton';
import { Spinner } from './Spinner';
import { LoadingState } from './States';

const SCOPE_LABEL: Record<IntegrationScope, string> = {
  'requests:write': 'Submit requests & feedback',
  'requests:read': 'Read request status',
  'feedback:write': 'File end-user feedback (write-only)',
  'prototypes:write': 'Create prototypes & file feedback',
  'prototypes:read': 'Read prototype status',
};

/** Write-only End-user feedback token (`feedback:write` and nothing else). */
const isFeedbackOnly = (t: IntegrationTokenDto) => t.scopes.every((s) => s === FEEDBACK_WRITE_SCOPE);

/** The freshly-minted token, shown once with a copy affordance. */
function NewTokenBanner({
  created,
  onDone,
}: {
  created: IntegrationTokenCreatedDto;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="card border-signal/40 bg-signal/5 p-4">
      <p className="mb-2 text-xs font-medium text-ink">
        Token "{created.name}" created. Copy it now: it is shown only once and
        cannot be retrieved again. Store it on your site's server, never in a browser.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="flex-1 select-all break-all rounded bg-surface px-2 py-1.5 font-mono text-xs text-ink">
          {created.token}
        </code>
        <button
          type="button"
          className="btn-secondary whitespace-nowrap"
          onClick={() => {
            void navigator.clipboard?.writeText(created.token);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="btn-primary whitespace-nowrap" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

/**
 * Owner-only Integration API tokens. With `projectId` (the project's Team
 * page): PROJECT tokens — an external site's backend submits build requests to
 * this project, polls them and files follow-up feedback. Without it (Workspace
 * settings): WORKSPACE "prototype factory" tokens — the site creates a
 * brand-new prototype per visitor request and sees only the prototypes it
 * created (docs/INTEGRATIONS.md). The plaintext is shown once; the server
 * stores only its hash.
 */
export function IntegrationTokensCard({ projectId }: { projectId?: string }) {
  const target = projectId ?? null;
  const available = integrationScopesForKind(target === null ? 'workspace' : 'project');
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const kind = target === null ? 'workspace' : 'project';
  // feedback:write is opt-in here — the End-user feedback card mints its own.
  const [scopes, setScopes] = useState<IntegrationScope[]>([...defaultIntegrationScopesForKind(kind)]);
  const [created, setCreated] = useState<IntegrationTokenCreatedDto | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<IntegrationTokenDto | null>(null);
  const [webhookOpen, setWebhookOpen] = useState<string | null>(null);

  const key = qk.integrationTokens(target);
  const { data: tokens, isLoading, isError, error } = useQuery({
    queryKey: key,
    queryFn: () => listIntegrationTokens(target),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const create = useMutation({
    // Canonical scope order, whatever order the boxes were ticked in.
    mutationFn: () =>
      createIntegrationToken(target, name.trim(), available.filter((s) => scopes.includes(s))),
    onSuccess: (res) => {
      setCreated(res);
      setName('');
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeIntegrationToken(target, id),
    onSuccess: () => {
      setConfirmRevoke(null);
      invalidate();
    },
  });

  const toggleScope = (s: IntegrationScope) =>
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const base = `${window.location.origin}/api/integrations/v1`;

  return (
    <section className="card p-5" aria-labelledby="integration-tokens-heading">
      <h2 id="integration-tokens-heading" className="mb-1 font-display text-sm font-bold text-ink">
        Integration tokens
      </h2>
      <p className="mb-3 text-xs text-ink-muted">
        {target === null ? (
          <>
            Let an external site's server create a <strong>brand-new prototype</strong> for each
            visitor request, follow it until it's live, and file the visitor's feedback on it, at{' '}
            <code className="text-ink-muted">{base}/prototypes</code>. Prototypes it creates count
            toward this workspace's prototype limit and stay until you delete them. A token sees{' '}
            <strong>only the prototypes it created</strong>, never acts as you, and stops working as
            soon as you revoke it.
          </>
        ) : (
          <>
            Let an external site (e.g. a "try an app" form) submit requests to this project, follow
            them until they're live, and send follow-up feedback, from its server, at{' '}
            <code className="text-ink-muted">{base}</code>. A token reaches{' '}
            <strong>only this project</strong>, never acts as you, and stops working as soon as you
            revoke it.
          </>
        )}
      </p>

      {target === null && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <CopyIntegrationPromptButton />
          <Link to="/docs#integration-api" className="text-xs font-medium text-ink underline">
            API docs
          </Link>
          <span className="text-xs text-ink-faint">
            Paste the prompt into the AI tool that builds your other site. It never includes a token.
          </span>
        </div>
      )}

      <div className="mb-4 space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <div className="flex-1">
            <input
              type="text"
              className="input"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              placeholder={target === null ? 'Name, e.g. “try-an-app form”' : 'Name, e.g. “services site”'}
              aria-label="Token name"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <button
            type="button"
            className="btn-primary whitespace-nowrap"
            disabled={create.isPending || !name.trim() || scopes.length === 0}
            onClick={() => create.mutate()}
          >
            {create.isPending && <Spinner className="h-3.5 w-3.5" />}
            Generate token
          </button>
        </div>
        <div className="flex flex-wrap gap-4">
          {available.map((s) => (
            <label key={s} className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
              <input
                type="checkbox"
                checked={scopes.includes(s)}
                onChange={() => toggleScope(s)}
              />
              {SCOPE_LABEL[s]}
            </label>
          ))}
        </div>
        {create.isError && (
          <Callout tone="danger">{errorMessage(create.error, 'Could not create the token')}</Callout>
        )}
      </div>

      {created && (
        <div className="mb-4">
          <NewTokenBanner created={created} onDone={() => setCreated(null)} />
        </div>
      )}

      {isLoading ? (
        <LoadingState label="Loading tokens…" />
      ) : isError ? (
        <Callout tone="danger">{errorMessage(error, 'Could not load the tokens')}</Callout>
      ) : tokens && tokens.length > 0 ? (
        <ul className="divide-y divide-border rounded border border-border">
          {tokens.map((t) => (
            <li key={t.id}>
              <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{t.name}</p>
                  <p className="text-xs text-ink-faint">
                    {t.scopes.map((s) => SCOPE_LABEL[s]).join(' · ')} · Created{' '}
                    {new Date(t.createdAt).toLocaleDateString()} ·{' '}
                    {t.lastUsedAt
                      ? `last used ${new Date(t.lastUsedAt).toLocaleString()}`
                      : 'never used'}
                    {t.webhookUrl ? ' · webhook on' : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* A write-only feedback token reads nothing, so it has no webhook. */}
                  {!isFeedbackOnly(t) && (
                    <button
                      type="button"
                      className="btn-secondary whitespace-nowrap"
                      aria-expanded={webhookOpen === t.id}
                      onClick={() => setWebhookOpen((cur) => (cur === t.id ? null : t.id))}
                    >
                      Webhook
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-danger-ghost whitespace-nowrap"
                    onClick={() => setConfirmRevoke(t)}
                  >
                    Revoke
                  </button>
                </div>
              </div>
              {webhookOpen === t.id && <IntegrationWebhookPanel projectId={target} token={t} />}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-faint">No integration tokens yet.</p>
      )}

      <ConfirmDialog
        open={confirmRevoke !== null}
        title="Revoke integration token"
        message={
          confirmRevoke
            ? `Revoke "${confirmRevoke.name}"? Any site using it gets 401 from its next request on. This cannot be undone.`
            : ''
        }
        confirmLabel="Revoke"
        busy={revoke.isPending}
        error={revoke.isError ? errorMessage(revoke.error, 'Could not revoke the token') : null}
        onConfirm={() => confirmRevoke && revoke.mutate(confirmRevoke.id)}
        onClose={() => {
          setConfirmRevoke(null);
          revoke.reset();
        }}
      />
    </section>
  );
}
